import type { CraterOperation, Point } from './types';

/** Old Tile.cs format: int32 LE width/height, then MSB-first one-bit pixels.
 * Original stride is floor(width / 8) + 1, including the spare byte at widths
 * divisible by eight. Compact ceil(width / 8) masks are accepted by length. */
export class Terrain {
  readonly width: number;
  readonly height: number;
  readonly stride: number;
  readonly data: Uint8Array;

  constructor(width: number, height: number, data?: Uint8Array, stride = Math.floor(width / 8) + 1) {
    if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 || width * height > 20_000_000) throw new Error('碰撞图尺寸无效');
    this.width = width; this.height = height; this.stride = stride;
    // Buffer extends Uint8Array but Buffer.slice() shares storage. Always copy
    // into a plain Uint8Array so digging a clone never edits original masks.
    this.data = data ? new Uint8Array(data) : new Uint8Array(stride * height);
    if (this.data.length !== stride * height) throw new Error('碰撞图数据长度不符');
  }

  static fromBuffer(input: ArrayBuffer | Uint8Array): Terrain {
    const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
    if (bytes.length < 8) throw new Error('碰撞图头部缺失');
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const width = view.getInt32(0, true), height = view.getInt32(4, true);
    const original = Math.floor(width / 8) + 1, compact = Math.ceil(width / 8);
    const stride = bytes.length - 8 === original * height ? original : compact;
    if (bytes.length - 8 !== stride * height) throw new Error('碰撞图数据长度不符');
    return new Terrain(width, height, bytes.subarray(8), stride);
  }

  toBuffer(): ArrayBuffer {
    const bytes = new Uint8Array(8 + this.data.length), view = new DataView(bytes.buffer);
    view.setInt32(0, this.width, true); view.setInt32(4, this.height, true);
    bytes.set(this.data, 8); return bytes.buffer;
  }

  clone(): Terrain { return new Terrain(this.width, this.height, this.data, this.stride); }

  isSolid(x: number, y: number): boolean {
    x = Math.floor(x); y = Math.floor(y);
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return false;
    return !!(this.data[y * this.stride + (x >> 3)] & (1 << (7 - (x & 7))));
  }

  setSolid(x: number, y: number, solid: boolean): void {
    x = Math.floor(x); y = Math.floor(y);
    if (x < 0 || y < 0 || x >= this.width || y >= this.height) return;
    const i = y * this.stride + (x >> 3), bit = 1 << (7 - (x & 7));
    this.data[i] = solid ? this.data[i] | bit : this.data[i] & ~bit;
  }

  /** Sweeps each pixel of a projectile segment to avoid tunnelling. */
  trace(a: Point, b: Point, radius = 2): Point | null {
    const n = Math.max(1, Math.ceil(Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y))));
    for (let i = 1; i <= n; i++) {
      const x = a.x + (b.x - a.x) * i / n, y = a.y + (b.y - a.y) * i / n;
      if (this.isSolid(x, y) || this.isSolid(x - radius, y) || this.isSolid(x + radius, y) || this.isSolid(x, y - radius) || this.isSolid(x, y + radius)) return { x: Math.round(x), y: Math.round(y) };
    }
    return null;
  }

  surfaceY(x: number, fromY = 0, halfWidth = 5): number | null {
    if (x < 0 || x >= this.width) return null;
    for (let y = Math.max(0, Math.floor(fromY)); y < this.height; y++) {
      if (this.isSolid(x, y) || this.isSolid(x - halfWidth, y) || this.isSolid(x + halfWidth, y)) return y - 1;
    }
    return null;
  }

  /** Erases the original crater mask rather than an invented circular hole. */
  dig(x: number, y: number, mask: Terrain): number {
    const left = Math.round(x) - Math.floor(mask.width / 2), top = Math.round(y) - Math.floor(mask.height / 2);
    let removed = 0;
    for (let my = Math.max(0, -top); my < Math.min(mask.height, this.height - top); my++) {
      for (let mx = Math.max(0, -left); mx < Math.min(mask.width, this.width - left); mx++) {
        if (mask.isSolid(mx, my) && this.isSolid(left + mx, top + my)) { this.setSolid(left + mx, top + my, false); removed++; }
      }
    }
    return removed;
  }

  replay(craters: CraterOperation[], masks: Map<string, Terrain>): void {
    for (const crater of craters) {
      const mask = masks.get(String(crater.id));
      if (!mask) throw new Error(`缺少弹坑碰撞数据：${crater.id}`);
      this.dig(crater.x, crater.y, mask);
    }
  }
}
